import { useState, useEffect, useRef } from "react";

function relativeTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  if (isNaN(date)) return ts;
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── INBOX-TRIAGE-1 helpers ───────────────────────────────────────────────────
// Plain-text snippet of an HTML body: tags stripped, whitespace collapsed, first N chars.
function snippetOf(html, n = 90) {
  const text = String(html || "").replace(/<style[\s\S]*?<\/style>/gi, " ").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/\s+/g, " ").trim();
  return text.length > n ? text.slice(0, n).trimEnd() + "…" : text;
}
// Initials: first letters of the first two words of the name, else the email's first letter.
function initialsOf(name, email) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length) return words.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  return String(email || "?")[0].toUpperCase();
}
const AVATAR_TINTS = [
  { bg: "rgba(45,212,191,.18)", fg: "#2dd4bf" },
  { bg: "rgba(224,160,80,.18)", fg: "#e0a050" },
  { bg: "rgba(180,140,242,.18)", fg: "#b48cf2" },
  { bg: "rgba(69,196,154,.18)", fg: "#45c49a" },
];
const avatarTint = (name) => AVATAR_TINTS[String(name || "").length % 4];
const shortDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const fullDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d) ? ts : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const QUOTE_RE = /<blockquote|gmail_quote|gmail_extra|yahoo_quoted|ms-outlook|divRplyFwdMsg|appendonsend/i;
const SIG_RE = /gmail_signature/i;
const needsReply = (r) => !r.last_is_outbound;

// Instantly provider_code (developer.instantly.ai, create-account): 1 custom IMAP/SMTP, 2 Google, 3 Microsoft.
// MAILBOX-DOMAIN-1: before this the modal sent 2 for every provider.
const PROVIDER_CODE = { google: 2, microsoft: 3, custom: 1 };
const PROVIDER_HOSTS = {
  google: { smtp_host: 'smtp.gmail.com', smtp_port: 587, imap_host: 'imap.gmail.com', imap_port: 993 },
  microsoft: { smtp_host: 'smtp.office365.com', smtp_port: 587, imap_host: 'outlook.office365.com', imap_port: 993 },
};

// Status vocabulary shared with ProjectView's select (plus 'pitched', shown but not settable).
const STATUS_OPTIONS = [["pending", "Pending"], ["engaged", "Engaged"], ["confirmed", "Confirmed"], ["no_interest", "No interest"]];
const STATUS_COLOR = { pending: "var(--st-pending)", pitched: "var(--st-pitched)", engaged: "var(--st-engaged)", confirmed: "var(--st-confirmed)", no_interest: "var(--st-no-interest)" };

// INBOX-FIT-1: timeline rows. The pitch and follow-up come from the
// project_experts stamps (outreach_sent_at / outreach_method / followup_sent_at),
// not from the thread — crm_replies only holds replies and our manual replies.
// Thread rows: inbound = "Replied", outbound = "You replied". Dated events are
// sorted; one undated row for where the ball is closes the list.
const METHOD_LABEL = { email: "email", linkedin: "LinkedIn", inmail: "InMail" };
function timelineOf(reply, messages, lastIsOutbound) {
  const t = (ts) => { const d = new Date(ts); return isNaN(d) ? 0 : d.getTime(); };
  const events = [];
  if (reply.outreach_sent_at) {
    const via = METHOD_LABEL[String(reply.outreach_method || "").toLowerCase()];
    events.push({ when: reply.outreach_sent_at, label: via ? `Pitch sent · ${via}` : "Pitch sent", tone: "normal" });
  }
  if (reply.followup_sent_at) events.push({ when: reply.followup_sent_at, label: "Follow-up sent", tone: "normal" });
  for (const m of messages) {
    if (!m.timestamp_email) continue;
    events.push(m.is_outbound
      ? { when: m.timestamp_email, label: "You replied", tone: "normal" }
      : { when: m.timestamp_email, label: "Replied", tone: "accent" });
  }
  events.sort((a, b) => t(a.when) - t(b.when));
  events.push({ when: null, label: lastIsOutbound ? "Waiting on them" : "Awaiting your reply", tone: "muted" });
  return events;
}

export default function CRMView({ API, currentUser, onNavigate }) {
  const [replies, setReplies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);        // INBOX-TRIAGE-1: server-side archived rows (?archived=1)
  const [crmFilter, setCrmFilter] = useState('needs');  // 'needs' | 'waiting' | 'all'
  const [crmSearch, setCrmSearch] = useState('');
  const [expandedMsgs, setExpandedMsgs] = useState(() => new Set());   // collapsed thread rows opened in place
  const [quotesOn, setQuotesOn] = useState(() => new Set());           // message ids showing quoted text
  const [sigOn, setSigOn] = useState(() => new Set());                 // message ids showing the signature
  const filterInitRef = useRef(false);
  const [hoveredReply, setHoveredReply] = useState(null);
  const [showSignaturePanel, setShowSignaturePanel] = useState(false);
  const [signature, setSignature] = useState('');
  const [draftSignature, setDraftSignature] = useState('');
  const composerRef = useRef(null);
  const threadRef = useRef(null);
  const [crmTab, setCrmTab] = useState('inbox');
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [addEmailForm, setAddEmailForm] = useState({
    email: '', first_name: '', last_name: '', password: '',
    provider: 'google', smtp_host: 'smtp.gmail.com', smtp_port: 587,
    imap_host: 'imap.gmail.com', imap_port: 993,
  });
  const [addEmailLoading, setAddEmailLoading] = useState(false);
  const [addEmailError, setAddEmailError] = useState('');
  const [enabledAccounts, setEnabledAccounts] = useState({});
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [thread, setThread] = useState([]);

  useEffect(() => {
    fetchReplies();
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (!selected) return;
    const token = localStorage.getItem('expertnet_token');
    fetch(`${API}/crm/thread/${selected.expert_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setThread(data.messages || []);
        setExpandedMsgs(new Set());
        setTimeout(() => {
          if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }, 0);
      })
      .catch(() => setThread([]));
  }, [selected]);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/instantly/accounts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAccounts(d.accounts || []); }
    } catch(e) {}
    const pref = await fetch(`${API}/user-email-accounts/${currentUser?.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } }).then(r => r.json()).catch(() => ({ accounts: [] }));
    const map = {};
    (pref.accounts || []).forEach(a => { map[a.email] = a.enabled; });
    setEnabledAccounts(map);
    setAccountsLoading(false);
  };

  const toggleAccount = async (email) => {
    const newVal = !(enabledAccounts[email] !== false);
    setEnabledAccounts(prev => ({ ...prev, [email]: newVal }));
    await fetch(`${API}/user-email-accounts/${currentUser?.id}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
      body: JSON.stringify({ email, enabled: newVal })
    });
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetch(`${API}/users/${currentUser.id}/signature`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } })
        .then(r => r.json())
        .then(data => { if (data.signature) setSignature(data.signature); })
        .catch(() => {});
    }
  }, [currentUser]);

  useEffect(() => {
    if (crmTab === 'accounts') fetchAccounts();
    if (crmTab === 'settings' && currentUser?.id) {
      fetch(`${API}/users/${currentUser.id}/signature`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } })
        .then(r => r.json())
        .then(data => { if (data.signature) setSignature(data.signature); })
        .catch(() => {});
    }
  }, [crmTab]);

  const handleAddEmail = async () => {
    if (!addEmailForm.email || !addEmailForm.password) {
      setAddEmailError('Email and password are required'); return;
    }
    setAddEmailLoading(true); setAddEmailError('');
    try {
      const body = {
        email: addEmailForm.email,
        first_name: addEmailForm.first_name,
        last_name: addEmailForm.last_name,
        provider_code: PROVIDER_CODE[addEmailForm.provider] || 1,
        smtp_username: addEmailForm.email,
        smtp_password: addEmailForm.password,
        smtp_host: addEmailForm.smtp_host,
        smtp_port: Number(addEmailForm.smtp_port),
        imap_username: addEmailForm.email,
        imap_password: addEmailForm.password,
        imap_host: addEmailForm.imap_host,
        imap_port: Number(addEmailForm.imap_port),
      };
      const res = await fetch(`${API}/instantly/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (res.ok) {
        setShowAddEmail(false);
        setAddEmailForm({ email: '', first_name: '', last_name: '', password: '', provider: 'google', smtp_host: 'smtp.gmail.com', smtp_port: 587, imap_host: 'imap.gmail.com', imap_port: 993 });
        fetchAccounts();
      } else {
        setAddEmailError(d.error || 'Failed to add account');
      }
    } catch(e) { setAddEmailError('Something went wrong'); }
    setAddEmailLoading(false);
  };

  // INBOX-TRIAGE-1: the inbox list and the archived list are two server views of
  // the same route (?archived=1). The default filter is decided once, from the
  // first inbox fetch: "Needs reply" when anything needs one, else "All".
  const fetchReplies = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` };
    try {
      const res = await fetch(`${API}/crm/replies`, { headers });
      if (res.ok) {
        const rows = await res.json();
        setReplies(rows);
        if (!filterInitRef.current) {
          filterInitRef.current = true;
          setCrmFilter(rows.some(needsReply) ? 'needs' : 'all');
        }
      }
    } catch (e) {
      console.error("Failed to fetch replies:", e);
    }
    try {
      const res = await fetch(`${API}/crm/replies?archived=1`, { headers });
      if (res.ok) setArchived(await res.json());
    } catch (e) {
      console.error("Failed to fetch archived replies:", e);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const selectReply = async (reply) => {
    setSelected(reply);

    if (!reply.is_read) {
      setReplies(prev => prev.map(r => r.id === reply.id ? { ...r, is_read: 1 } : r));
      setSelected(prev => prev && prev.id === reply.id ? { ...prev, is_read: 1 } : prev);
      try {
        await fetch(`${API}/crm/replies/${reply.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
          body: JSON.stringify({ is_read: 1 }),
        });
      } catch (e) {
        console.error("Failed to mark as read:", e);
      }
    }
  };

  const archiveReply = async (e, reply) => {
    if (e) e.stopPropagation();
    setReplies(prev => prev.filter(r => r.id !== reply.id));
    setArchived(prev => [{ ...reply, is_archived: 1 }, ...prev.filter(r => r.id !== reply.id)]);
    if (selected?.id === reply.id) setSelected(null);
    try {
      await fetch(`${API}/crm/replies/${reply.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ is_archived: 1 }),
      });
    } catch (e) {
      console.error("Failed to archive reply:", e);
    }
  };

  const applyFormat = (tag) => {
    const composer = composerRef.current;
    if (!composer) return;
    composer.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const el = document.createElement(tag);
    try {
      range.surroundContents(el);
    } catch {
      el.appendChild(range.extractContents());
      range.insertNode(el);
    }
    sel.removeAllRanges();
  };

  // INBOX-TRIAGE-2: the rail's Status select. PATCH /api/experts/<id>/status takes
  // the project_experts.id (not experts.id) — the row carries it as project_expert_id.
  const updateStatus = async (reply, status) => {
    if (!reply?.project_expert_id) { showToast("This expert isn't on a project yet", "error"); return; }
    const apply = (r) => (r.id === reply.id ? { ...r, project_expert_status: status } : r);
    setReplies(prev => prev.map(apply));
    setArchived(prev => prev.map(apply));
    setSelected(prev => (prev && prev.id === reply.id ? { ...prev, project_expert_status: status } : prev));
    try {
      const res = await fetch(`${API}/experts/${reply.project_expert_id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) showToast("Status did not save. Try again.", "error");
    } catch (e) {
      showToast("Status did not save. Try again.", "error");
    }
  };

  // SIG-SAVE-1: one save path for both editors (Settings tab and the Inbox drawer).
  // Returns true only when the server accepted the signature; the pitch path reads
  // users.email_signature, so a signature that lives only in page state is not sent.
  const saveSignature = async (value) => {
    const sig = value === undefined ? signature : value;
    setSignatureError('');
    try {
      const res = await fetch(`${API}/users/${currentUser.id}/signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ signature: sig })
      });
      if (!res.ok) {
        setSignatureError('Signature did not save. Try again.');
        return false;
      }
    } catch (e) {
      setSignatureError('Signature did not save. Try again.');
      return false;
    }
    setSignature(sig);
    setSignatureSaved(true);
    setTimeout(() => setSignatureSaved(false), 2000);
    return true;
  };

  const sendReply = async () => {
    if (!selected || !composerRef.current) return;
    let body = composerRef.current.innerHTML.trim();
    if (includeSignature && signature) {
      body = body + '<br><br>' + signature.replace(/\n/g, '<br>');
    }
    if (!body) { showToast("Reply body is empty", "error"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/crm/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({
          eaccount: selected.eaccount,
          reply_to_uuid: selected.instantly_email_id,
          subject: "Re: " + (selected.subject || ""),
          body,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast("Reply sent!");
        composerRef.current.innerHTML = "";
        // Newest message is now ours: the row moves to "Waiting on them".
        setSelected(prev => ({ ...prev, is_read: 1, last_is_outbound: 1 }));
        setReplies(prev => prev.map(r => r.id === selected.id ? { ...r, is_read: 1, last_is_outbound: 1 } : r));
      } else {
        showToast(data.error || "Failed to send reply", "error");
      }
    } catch (e) {
      showToast("Failed to send reply", "error");
    } finally {
      setSending(false);
    }
  };

  // ── INBOX-TRIAGE-1: derived lists ──
  const needsCount = replies.filter(needsReply).length;
  const waitingCount = replies.length - needsCount;
  const q = crmSearch.trim().toLowerCase();
  const matchesSearch = (r) => !q || [r.expert_name, r.from_email, r.project_name, r.subject]
    .some((v) => String(v || "").toLowerCase().includes(q));
  const inboxRows = replies.filter(matchesSearch);
  const needsRows = inboxRows.filter(needsReply);
  const waitingRows = inboxRows.filter((r) => !needsReply(r));
  const visibleReplies = showArchived
    ? archived.filter(matchesSearch)
    : crmFilter === 'needs' ? needsRows : crmFilter === 'waiting' ? waitingRows : [...needsRows, ...waitingRows];
  const selectedIdx = selected ? visibleReplies.findIndex((r) => r.id === selected.id) : -1;
  const moveSelection = (delta) => {
    if (!visibleReplies.length) return;
    const next = selectedIdx < 0 ? (delta > 0 ? 0 : visibleReplies.length - 1) : Math.min(visibleReplies.length - 1, Math.max(0, selectedIdx + delta));
    if (visibleReplies[next] && visibleReplies[next].id !== selected?.id) selectReply(visibleReplies[next]);
  };
  const archiveSelected = () => {
    if (!selected || showArchived) return;
    const nextRow = visibleReplies[selectedIdx + 1] || visibleReplies[selectedIdx - 1] || null;
    archiveReply(null, selected);
    if (nextRow) selectReply(nextRow);
  };

  // Keyboard (3.8): on the inbox tab, when focus is not in an editable field —
  // ↑/↓ move the selection, r focuses the composer, e archives.
  useEffect(() => {
    if (crmTab !== 'inbox') return undefined;
    const onKey = (e) => {
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); moveSelection(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); moveSelection(-1); }
      else if (e.key === 'r' && selected && composerRef.current) { e.preventDefault(); composerRef.current.focus(); }
      else if (e.key === 'e' && selected) { e.preventDefault(); archiveSelected(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // no deps: reads the latest closure each render

  const pill = (active) => ({
    height: 26, padding: "0 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 5,
    border: `1px solid ${active ? "rgba(45,212,191,.4)" : "var(--line)"}`,
    background: active ? "rgba(45,212,191,.12)" : "transparent",
    color: active ? "var(--text-1)" : "var(--text-2)",
  });
  const mono = { fontFamily: "var(--font-data)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Tab bar — filters folded in (INBOX-TRIAGE-1) */}
      <div style={{ borderBottom: "1px solid var(--line)", padding: "0 24px", display: "flex", alignItems: "center", gap: 4, flexShrink: 0, flexWrap: "wrap" }}>
        {['inbox', 'accounts', 'settings'].map(t => (
          <button
            key={t}
            onClick={() => setCrmTab(t)}
            style={{
              padding: "12px 14px", fontSize: 13, fontWeight: 500,
              background: "transparent", border: "none", cursor: "pointer",
              textTransform: "capitalize",
              color: crmTab === t ? "var(--text-1)" : "var(--text-3)",
              borderBottom: crmTab === t ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: -1,
            }}
          >{t === 'inbox' ? 'Inbox' : t === 'accounts' ? 'Accounts' : 'Settings'}</button>
        ))}
        {crmTab === 'inbox' && (
          <>
            <span aria-hidden="true" style={{ width: 1, height: 18, background: "var(--line)", margin: "0 10px" }} />
            <button type="button" aria-pressed={!showArchived && crmFilter === 'needs'} style={pill(!showArchived && crmFilter === 'needs')} onClick={() => { setShowArchived(false); setCrmFilter('needs'); }}>
              Needs reply <span style={{ ...mono, color: "var(--warning)" }}>{needsCount}</span>
            </button>
            <button type="button" aria-pressed={!showArchived && crmFilter === 'waiting'} style={pill(!showArchived && crmFilter === 'waiting')} onClick={() => { setShowArchived(false); setCrmFilter('waiting'); }}>
              Waiting on them <span style={{ ...mono, color: "var(--text-3)" }}>{waitingCount}</span>
            </button>
            <button type="button" aria-pressed={!showArchived && crmFilter === 'all'} style={pill(!showArchived && crmFilter === 'all')} onClick={() => { setShowArchived(false); setCrmFilter('all'); }}>
              All <span style={{ ...mono, color: "var(--text-3)" }}>{replies.length}</span>
            </button>
          </>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          {accounts.length > 0 && (() => {
            const totalSent  = accounts.reduce((s, a) => s + (a.sent_today || 0), 0);
            const totalLimit = accounts.reduce((s, a) => s + (a.daily_limit || 0), 0);
            const ratio = totalLimit > 0 ? totalSent / totalLimit : 0;
            const color = ratio >= 0.9 ? 'var(--negative)' : ratio >= 0.7 ? 'var(--warning)' : 'var(--text-2)';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                title={`Across ${accounts.length} mailbox${accounts.length === 1 ? '' : 'es'}`}>
                <span className="r-eyebrow">Email</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color }}>
                  {totalSent}/{totalLimit} today
                </span>
              </div>
            );
          })()}
          {crmTab === 'inbox' && (
            <button
              className="r-btn r-btn-ghost"
              style={{ height: 26, fontSize: 12, padding: "0 10px" }}
              onClick={() => { setDraftSignature(signature); setShowSignaturePanel(true); }}
            >Signature</button>
          )}
        </div>
      </div>

      {crmTab === 'inbox' && (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>

        {/* Panes: list · thread (the expert rail is INBOX-TRIAGE-2) */}
        {/* INBOX-FIT-1: one row bounded by the viewport (no wrap, no outer scroll), so the
            list and the thread scroll inside their panes and the composer stays in view. */}
        <div style={{ display: "flex", flexWrap: "nowrap", gap: 12, padding: "14px 24px 24px", flex: 1, minHeight: 0, alignItems: "stretch" }}>

          {/* List pane */}
          <div className="r-card" style={{ flex: "0 1 280px", minWidth: 220, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {/* List header: search + count */}
            <label style={{ height: 40, padding: "0 12px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-4)" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
              <input
                type="search"
                placeholder="Search"
                aria-label="Search replies"
                value={crmSearch}
                onChange={(e) => setCrmSearch(e.target.value)}
                style={{ flex: 1, minWidth: 0, border: 0, background: "transparent", color: "var(--text-1)", fontSize: 12, fontFamily: "inherit", outline: "none" }}
              />
              <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)", whiteSpace: "nowrap" }}>
                {showArchived ? `${visibleReplies.length} archived` : `${needsCount} need reply`}
              </span>
            </label>
            {/* List body */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              {visibleReplies.length === 0 ? (
                <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-4)", fontSize: 13 }}>
                  {showArchived ? "No archived replies." : q ? "No replies match your search." : crmFilter === 'needs' ? "Nothing needs a reply." : crmFilter === 'waiting' ? "Nothing is waiting on them." : "No replies yet. Replies from your outreach campaigns will appear here."}
                </div>
              ) : (
                visibleReplies.map((reply, idx) => {
                  const isSelected = selected?.id === reply.id;
                  const needs = !showArchived && needsReply(reply);
                  const isHovered = hoveredReply === reply.id;
                  const tint = avatarTint(reply.expert_name || reply.from_email);
                  const groupHead = !showArchived && crmFilter === 'all' && idx === needsRows.length && waitingRows.length > 0;
                  return (
                    <div key={reply.id}>
                      {groupHead && (
                        <div className="r-eyebrow" style={{ padding: "8px 12px 4px" }}>Waiting on them</div>
                      )}
                      <div
                        onClick={() => selectReply(reply)}
                        onMouseEnter={() => setHoveredReply(reply.id)}
                        onMouseLeave={() => setHoveredReply(null)}
                        style={{
                          display: "grid", gridTemplateColumns: "28px 1fr",
                          gap: "0 10px",
                          padding: "10px 12px",
                          borderBottom: "1px solid var(--line-soft)",
                          cursor: "pointer",
                          background: isSelected ? "rgba(45,212,191,.08)" : "transparent",
                          boxShadow: isSelected ? "inset 2px 0 0 var(--accent)" : "none",
                          opacity: !showArchived && !needs ? 0.75 : 1,
                          transition: "background 0.12s",
                        }}
                        onMouseOver={e => { if (!isSelected) e.currentTarget.style.background = "var(--row-hover)"; }}
                        onMouseOut={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                      >
                        {/* Col 1: initials avatar */}
                        <span aria-hidden="true" style={{
                          width: 28, height: 28, borderRadius: "50%", background: tint.bg, color: tint.fg,
                          fontSize: 10, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2,
                        }}>
                          {initialsOf(reply.expert_name, reply.from_email)}
                        </span>
                        {/* Col 2: name · dot · time / snippet / project */}
                        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: needs ? 600 : 500, color: "var(--text-1)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {reply.expert_name || reply.from_email || "Unknown"}
                            </span>
                            {needs && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--warning)", flexShrink: 0 }} />}
                            {isHovered && !showArchived ? (
                              <button
                                onClick={(e) => archiveReply(e, reply)}
                                title="Archive"
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)", lineHeight: 1 }}
                                onMouseOver={e => e.currentTarget.style.color = "var(--text-1)"}
                                onMouseOut={e => e.currentTarget.style.color = "var(--text-4)"}
                              >Archive</button>
                            ) : (
                              <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)", flexShrink: 0 }}>
                                {relativeTime(reply.timestamp_email)}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 12, color: needs ? "var(--text-2)" : "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {snippetOf(reply.body) || reply.subject || "(no body)"}
                          </span>
                          <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: reply.project_name ? "var(--accent)" : "var(--text-5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {reply.project_name || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {!showArchived && (
                <div
                  onClick={() => { setShowArchived(true); fetchReplies(); }}
                  style={{ padding: "10px 14px", cursor: "pointer", fontSize: 12, color: "var(--text-4)", borderTop: "1px solid var(--line-soft)", textAlign: "center" }}
                  onMouseOver={e => e.currentTarget.style.color = "var(--text-1)"}
                  onMouseOut={e => e.currentTarget.style.color = "var(--text-4)"}
                >
                  Archived ({archived.length})
                </div>
              )}
              {showArchived && (
                <div
                  onClick={() => setShowArchived(false)}
                  style={{ padding: "10px 14px", cursor: "pointer", fontSize: 12, color: "var(--accent)", borderTop: "1px solid var(--line-soft)", textAlign: "center" }}
                >
                  ← Back to Inbox
                </div>
              )}
            </div>
          </div>

          {/* Reader pane */}
          <div className="r-card" style={{ flex: "1 1 420px", minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {!selected ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <span style={{ fontSize: 28, color: "var(--text-5)" }}>✉</span>
                <span style={{ fontSize: 13, color: "var(--text-4)" }}>Select a reply to view</span>
              </div>
            ) : (() => {
              const who = selected.expert_name || selected.from_email;
              // Thread: the server's messages, or the selected reply alone when the thread is empty.
              const msgs = thread.length > 0 ? thread : [{
                id: `reply-${selected.id}`, body: selected.body || '<em>No body content.</em>', is_outbound: 0,
                timestamp_email: selected.timestamp_email, from_email: selected.from_email, eaccount: selected.eaccount,
              }];
              const lastIdx = msgs.length - 1;
              const tint = avatarTint(who);
              const toggleIn = (setter) => (id) => setter((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
              const toggleExpanded = toggleIn(setExpandedMsgs);
              const toggleQuotes = toggleIn(setQuotesOn);
              const toggleSig = toggleIn(setSigOn);
              const openCard = (msg) => {
                const body = msg.body || "";
                const hasQuotes = QUOTE_RE.test(body);
                const hasSig = SIG_RE.test(body);
                const cls = `crm-body${quotesOn.has(msg.id) ? " crm-body--quotes" : ""}${sigOn.has(msg.id) ? " crm-body--sig" : ""}`;
                return (
                  <article key={msg.id} style={{
                    padding: "14px 16px 10px", borderRadius: 10,
                    border: `1px solid ${msg.is_outbound ? "rgba(45,212,191,.35)" : "rgba(45,212,191,.25)"}`,
                    background: msg.is_outbound ? "rgba(45,212,191,.10)" : "rgba(45,212,191,.05)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                      <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: "50%", background: msg.is_outbound ? "rgba(45,212,191,.18)" : tint.bg, color: msg.is_outbound ? "var(--accent)" : tint.fg, fontSize: 10, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                        {msg.is_outbound ? "ME" : initialsOf(selected.expert_name, selected.from_email)}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: msg.is_outbound ? "var(--accent)" : "var(--text-1)" }}>{msg.is_outbound ? "You" : who}</span>
                      <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{msg.is_outbound ? (msg.eaccount || selected.eaccount) : (msg.from_email || selected.from_email)}</span>
                      <span style={{ marginLeft: "auto", fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", whiteSpace: "nowrap" }}>{fullDate(msg.timestamp_email)}</span>
                    </div>
                    <div className={cls} style={{ maxWidth: 640, overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: body }} />
                    {(hasQuotes || hasSig) && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        {hasQuotes && (
                          <button type="button" className="r-btn r-btn-ghost" style={{ height: 24, fontSize: 11, padding: "0 8px", color: "var(--text-3)" }} onClick={() => toggleQuotes(msg.id)}>
                            ··· {quotesOn.has(msg.id) ? "Hide quoted text" : "Show quoted text"}
                          </button>
                        )}
                        {hasSig && (
                          <button type="button" className="r-btn r-btn-ghost" style={{ height: 24, fontSize: 11, padding: "0 8px", color: "var(--text-3)" }} onClick={() => toggleSig(msg.id)}>
                            {sigOn.has(msg.id) ? "Hide signature" : "Show signature"}
                          </button>
                        )}
                      </div>
                    )}
                  </article>
                );
              };
              return (
              <>
                {/* Reader header */}
                <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", flex: 1, minWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selected.subject || who}
                  </span>
                  <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", whiteSpace: "nowrap" }}>
                    {msgs.length} {msgs.length === 1 ? "message" : "messages"}
                  </span>
                  {!showArchived && (
                    <button type="button" className="r-btn r-btn-ghost" style={{ height: 28, fontSize: 12, padding: "0 10px" }} onClick={archiveSelected}>Archive</button>
                  )}
                  <button type="button" className="r-btn r-btn-ghost" aria-label="Previous conversation" style={{ width: 28, height: 28, padding: 0, fontSize: 12 }} onClick={() => moveSelection(-1)} disabled={selectedIdx <= 0}>↑</button>
                  <button type="button" className="r-btn r-btn-ghost" aria-label="Next conversation" style={{ width: 28, height: 28, padding: 0, fontSize: 12 }} onClick={() => moveSelection(1)} disabled={selectedIdx < 0 || selectedIdx >= visibleReplies.length - 1}>↓</button>
                </div>

                {/* Thread: earlier messages collapsed to one line, the newest open */}
                <div ref={threadRef} style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {msgs.map((msg, i) => (
                    i < lastIdx && !expandedMsgs.has(msg.id) ? (
                      <button
                        key={msg.id}
                        type="button"
                        onClick={() => toggleExpanded(msg.id)}
                        title="Show this message"
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line-soft)", background: "rgba(255,255,255,.02)", color: "inherit", fontFamily: "inherit", textAlign: "left", cursor: "pointer" }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 500, color: msg.is_outbound ? "var(--accent)" : "var(--text-1)", whiteSpace: "nowrap" }}>{msg.is_outbound ? "You" : who}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{snippetOf(msg.body, 120)}</span>
                        <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", whiteSpace: "nowrap" }}>{shortDate(msg.timestamp_email)}</span>
                      </button>
                    ) : openCard(msg)
                  ))}
                </div>

                {/* Composer — always docked (INBOX-TRIAGE-1); manual replies only, no suggestion chips (INBOX-MANUAL-1) */}
                <div style={{ borderTop: "1px solid var(--line)", padding: "10px 18px 12px", display: "flex", flexDirection: "column", gap: 8, background: "rgba(0,0,0,.12)", flexShrink: 0 }}>
                  <div
                    ref={composerRef}
                    className="crm-composer"
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    aria-label="Reply body"
                    aria-multiline="true"
                    data-placeholder={`Reply to ${who}…`}
                    style={{
                      minHeight: 56, padding: "10px 12px",
                      background: "var(--input-bg)", border: "1px solid var(--line)",
                      borderRadius: 8, color: "var(--text-1)", fontSize: 13, lineHeight: 1.6,
                      overflowY: "auto", outline: "none", maxHeight: 220,
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = "rgba(45,212,191,.5)"; }}
                    onBlur={e => { e.currentTarget.style.borderColor = "var(--line)"; }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {[["B", "b"], ["I", "i"]].map(([label, tag]) => (
                      <button
                        key={tag}
                        type="button"
                        className="r-btn r-btn-ghost"
                        aria-label={tag === "b" ? "Bold" : "Italic"}
                        onMouseDown={(e) => { e.preventDefault(); applyFormat(tag); }}
                        style={{ width: 26, height: 26, padding: 0, fontSize: 12, fontWeight: 700, fontStyle: tag === "i" ? "italic" : "normal" }}
                      >{label}</button>
                    ))}
                    <label htmlFor="sig-toggle" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-3)", cursor: "pointer", marginLeft: 6 }}>
                      <input type="checkbox" id="sig-toggle" checked={includeSignature} onChange={e => setIncludeSignature(e.target.checked)} style={{ accentColor: "var(--accent)", margin: 0 }} />
                      Signature
                    </label>
                    <div style={{ flex: 1 }} />
                    <button
                      type="button"
                      className="r-btn r-btn-primary"
                      onClick={sendReply}
                      disabled={sending}
                      style={{ height: 30, fontSize: 12, ...(sending ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                    >
                      {sending ? "Sending…" : "Send reply"}
                    </button>
                  </div>
                </div>
              </>
              );
            })()}
          </div>

          {/* Expert rail (INBOX-TRIAGE-2) */}
          {selected && (() => {
            const who = selected.expert_name || selected.from_email;
            const tint = avatarTint(who);
            const status = selected.project_expert_status || "pending";
            const divider = <div style={{ height: 1, background: "rgba(130,210,220,.10)" }} />;
            const rows = timelineOf(selected, thread.length ? thread : [{ is_outbound: 0, timestamp_email: selected.timestamp_email }], !!selected.last_is_outbound);
            return (
              <aside aria-label="Expert" className="r-card" style={{ flex: "0 0 256px", minHeight: 0, padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: "50%", background: tint.bg, color: tint.fg, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                    {initialsOf(selected.expert_name, selected.from_email)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{who}</div>
                    {(selected.expert_title || selected.expert_company) && (
                      <div data-testid="rail-title" style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {[selected.expert_title, selected.expert_company].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {selected.expert_location && (
                      <div style={{ fontSize: 11, color: "var(--text-4)" }}>{selected.expert_location}</div>
                    )}
                  </div>
                </div>
                {selected.expert_linkedin_url && (
                  <a href={selected.expert_linkedin_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "var(--accent)", textDecoration: "none" }}>
                    Open LinkedIn profile ↗
                  </a>
                )}

                {divider}

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div className="r-eyebrow">Project</div>
                  <div style={{ fontSize: 12, color: selected.project_name ? "var(--text-2)" : "var(--text-4)" }}>{selected.project_name || "No project"}</div>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-4)" }}>
                    Status
                    <select
                      data-testid="rail-status"
                      value={STATUS_OPTIONS.some(([v]) => v === status) ? status : ""}
                      onChange={(e) => updateStatus(selected, e.target.value)}
                      disabled={!selected.project_expert_id}
                      style={{ height: 30, padding: "0 8px", borderRadius: 8, border: "1px solid var(--line)", background: "var(--input-bg)", color: STATUS_COLOR[status] || "var(--st-pending)", fontSize: 12, fontFamily: "inherit", outline: "none" }}
                    >
                      {status === "pitched" && <option value="" disabled>Pitched</option>}
                      {STATUS_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                    </select>
                  </label>
                </div>

                <button type="button" className="r-btn r-btn-primary" style={{ height: 32, fontSize: 12 }} onClick={() => onNavigate && onNavigate("scheduling")} disabled={!onNavigate}>
                  Schedule call
                </button>

                {divider}

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="rail-timeline">
                  <div className="r-eyebrow">Timeline</div>
                  {rows.map((r, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, fontSize: 12 }}>
                      <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)", width: 40, flex: "none" }}>{r.when ? shortDate(r.when) : "—"}</span>
                      <span style={{ color: r.tone === "accent" ? "var(--accent)" : r.tone === "muted" ? "var(--text-4)" : "var(--text-2)" }}>{r.label}</span>
                    </div>
                  ))}
                </div>
              </aside>
            );
          })()}

        </div>
      </div>
      )}

      {crmTab === 'accounts' && (
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button className="r-btn r-btn-primary" onClick={() => setShowAddEmail(true)}>Add Email Account</button>
          </div>
          {accountsLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
              <span style={{ color: "var(--text-3)", fontSize: 13 }}>Loading...</span>
            </div>
          ) : accounts.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
              <span style={{ color: "var(--text-3)", fontSize: 13 }}>No email accounts connected.</span>
            </div>
          ) : (
            <div className="r-card">
              {accounts.map((acct, idx) => {
                const pct = acct.daily_limit > 0 ? (acct.sent_today / acct.daily_limit) * 100 : 0;
                const fillColor = acct.sent_today === 0 ? 'var(--positive)' : pct >= 100 ? 'var(--negative)' : 'var(--accent)';
                return (
                  <div key={acct.email} style={{
                    display: "flex", alignItems: "center", height: 56,
                    padding: "0 18px",
                    borderBottom: idx < accounts.length - 1 ? "1px solid var(--line-soft)" : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--font-data)", fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acct.email}</div>
                      <div style={{ width: "100%", height: 3, background: "var(--line-soft)", borderRadius: 2, marginTop: 4 }}>
                        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: fillColor, borderRadius: 2 }} />
                      </div>
                    </div>
                    <div style={{ marginLeft: 20, textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "var(--font-data)", fontSize: 12 }}>
                        <span style={{ color: "var(--accent)" }}>{acct.sent_today}</span>
                        <span style={{ color: "var(--text-4)" }}> / {acct.daily_limit}</span>
                      </div>
                      {acct.health_score != null && (
                        <div style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--text-4)", marginTop: 2 }}>{acct.health_score}%</div>
                      )}
                    </div>
                    <div
                      onClick={() => toggleAccount(acct.email)}
                      style={{ marginLeft: 16, width: 34, height: 20, borderRadius: 10, background: enabledAccounts[acct.email] !== false ? 'rgba(45,212,191,.6)' : 'rgba(255,255,255,.12)', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}
                    >
                      <div style={{ position: 'absolute', top: 2, left: enabledAccounts[acct.email] !== false ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {crmTab === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <div className="r-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>Email Signature</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>Added to the bottom of every outreach email.</div>
            <textarea
              value={signature}
              onChange={e => setSignature(e.target.value)}
              style={{ width: '100%', height: 120, background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--text-1)', fontSize: 13, padding: 12, boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
            />
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="r-btn r-btn-primary" data-testid="sig-settings-save" onClick={() => saveSignature()}>Save Signature</button>
              {signatureSaved && <span style={{ color: 'var(--positive)', fontSize: 12 }}>Saved</span>}
              {signatureError && <span data-testid="sig-settings-error" style={{ color: 'var(--negative)', fontSize: 12 }}>{signatureError}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Add Email Account Modal */}
      {showAddEmail && (() => {
        const modalInput = {
          width: "100%", background: "var(--input-bg)", border: "1px solid var(--line)",
          borderRadius: 8, color: "var(--text-1)", fontSize: 13, height: 34, padding: "0 12px",
          outline: "none", boxSizing: "border-box", marginBottom: 14,
        };
        const isCustom = addEmailForm.provider === 'custom';
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(2,7,13,.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div style={{ background: "var(--glass-card)", border: "1px solid var(--line)", borderRadius: 12, padding: 28, maxWidth: 480, width: "100%", boxShadow: "var(--card-shadow)", backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)" }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-1)", marginBottom: 20 }}>Add Email Account</div>

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>Email Address</div>
              <input type="email" style={modalInput} value={addEmailForm.email} onChange={e => setAddEmailForm(f => ({ ...f, email: e.target.value }))} />

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>First Name</div>
              <input style={modalInput} value={addEmailForm.first_name} onChange={e => setAddEmailForm(f => ({ ...f, first_name: e.target.value }))} />

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>Last Name</div>
              <input style={modalInput} value={addEmailForm.last_name} onChange={e => setAddEmailForm(f => ({ ...f, last_name: e.target.value }))} />

              <div className="r-eyebrow" style={{ marginBottom: 6 }}>App Password</div>
              <input type="password" style={modalInput} value={addEmailForm.password} onChange={e => setAddEmailForm(f => ({ ...f, password: e.target.value }))} />
              <div style={{ color: "var(--text-4)", fontSize: 11, marginTop: -10, marginBottom: 14 }}>
                {addEmailForm.provider === 'google'
                  ? 'Gmail: turn on 2-Step Verification, then create an App Password (Google Account → Security). Your normal password will not work.'
                  : addEmailForm.provider === 'microsoft'
                    ? 'Outlook / Microsoft 365: use an App Password, and IMAP must be enabled on the mailbox.'
                    : 'Use the password your mail provider gives for IMAP/SMTP access.'}
              </div>

              <div style={{ marginTop: 12, marginBottom: 16 }}>
                <div className="r-eyebrow" style={{ marginBottom: 8 }}>Provider</div>
                <div className="r-seg" style={{ display: "inline-flex", gap: 2 }}>
                  {[['google', 'Google / Gmail'], ['microsoft', 'Microsoft / Outlook'], ['custom', 'Custom SMTP']].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setAddEmailForm(f => ({
                        ...f,
                        provider: val,
                        ...(PROVIDER_HOSTS[val] || {}),
                      }))}
                      className={addEmailForm.provider === val ? 'r-seg-on' : ''}
                      style={{ padding: "5px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer", border: "none", background: "transparent", color: addEmailForm.provider === val ? "var(--text-1)" : "var(--text-3)", borderRadius: 6 }}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {isCustom && (
                <>
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>SMTP Host</div>
                  <input style={modalInput} value={addEmailForm.smtp_host} onChange={e => setAddEmailForm(f => ({ ...f, smtp_host: e.target.value }))} />
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>SMTP Port</div>
                  <input type="number" style={modalInput} value={addEmailForm.smtp_port} onChange={e => setAddEmailForm(f => ({ ...f, smtp_port: e.target.value }))} />
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>IMAP Host</div>
                  <input style={modalInput} value={addEmailForm.imap_host} onChange={e => setAddEmailForm(f => ({ ...f, imap_host: e.target.value }))} />
                  <div className="r-eyebrow" style={{ marginBottom: 6 }}>IMAP Port</div>
                  <input type="number" style={modalInput} value={addEmailForm.imap_port} onChange={e => setAddEmailForm(f => ({ ...f, imap_port: e.target.value }))} />
                </>
              )}

              {addEmailError && <div style={{ color: "var(--negative)", fontSize: 12, marginBottom: 12 }}>{addEmailError}</div>}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  className="r-btn r-btn-ghost"
                  onClick={() => { setShowAddEmail(false); setAddEmailError(''); }}
                >Cancel</button>
                <button
                  className="r-btn r-btn-primary"
                  onClick={handleAddEmail}
                  disabled={addEmailLoading}
                  style={addEmailLoading ? { opacity: 0.7, cursor: "not-allowed" } : {}}
                >{addEmailLoading ? "Adding..." : "Add Account"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Signature Settings Drawer */}
      {showSignaturePanel && (
        <>
          <div
            onClick={() => setShowSignaturePanel(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(2,7,13,.72)", zIndex: 1000 }}
          />
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: 360,
            background: "var(--glass-card)", borderLeft: "1px solid var(--line)",
            backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
            display: "flex", flexDirection: "column", zIndex: 1001,
            boxShadow: "-4px 0 24px rgba(0,0,0,0.4)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 18px 14px", borderBottom: "1px solid var(--line)" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>Inbox Settings</span>
              <button
                className="r-btn r-btn-ghost"
                onClick={() => setShowSignaturePanel(false)}
                aria-label="Close"
                style={{ width: 24, height: 24, padding: 0, fontSize: 13 }}
              >✕</button>
            </div>
            <div style={{ flex: 1, padding: "18px", display: "flex", flexDirection: "column", gap: 12 }}>
              <span className="r-eyebrow">Email Signature</span>
              <textarea
                value={draftSignature}
                onChange={e => setDraftSignature(e.target.value)}
                placeholder="e.g. Best,&#10;Cameron Hinkle"
                rows={6}
                style={{
                  background: "var(--input-bg)", border: "1px solid var(--line)",
                  borderRadius: 8, color: "var(--text-1)", fontSize: 13, lineHeight: 1.6,
                  padding: "10px 12px", resize: "vertical", outline: "none",
                }}
                onFocus={e => { e.currentTarget.style.borderColor = "rgba(45,212,191,.5)"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "var(--line)"; }}
              />
              {signatureError && <div data-testid="sig-drawer-error" style={{ color: "var(--negative)", fontSize: 12 }}>{signatureError}</div>}
              <button
                className="r-btn r-btn-primary"
                data-testid="sig-drawer-save"
                onClick={async () => { if (await saveSignature(draftSignature)) setShowSignaturePanel(false); }}
                style={{ alignSelf: "flex-end" }}
              >Save</button>
            </div>
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: "var(--glass-card)", border: "1px solid var(--line)",
          color: toast.type === "error" ? "var(--negative)" : "var(--positive)",
          padding: "10px 18px", borderRadius: 8, fontSize: 13,
          zIndex: 9999, boxShadow: "var(--card-shadow)",
          backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span className="r-dot" style={{ color: toast.type === "error" ? "var(--negative)" : "var(--positive)" }} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}
